import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useInjectReducer } from '../useInjectReducer';
import {
  hasMoreJobsSelector,
  isJobsErroredSelector,
  isJobsInitializedSelector,
  jobsSelector,
  key,
  totalJobsSelector,
} from './selectors';
import reducer from './reducer';
import { useInjectSaga } from '../useInjectSaga';
import saga from './sagas';
import { makeSelectIsLoading } from '../../redux/loadingSelector';
import { loadJobs, resetJobsList } from './actions';
import { qsParse, qsStringify } from '../../helpers/queryString';
import { useJobsFilters, DEFAULT_FILTER } from './useJobsFilters';

const defaultHitsPerPage = 20;

const useJobs = ({ forCompany, includeMatchesLists = false, isCompanyProfile = false }) => {
  const history = useHistory();
  const location = useLocation();
  useInjectReducer({ key, reducer });
  useInjectSaga({ key, saga });
  const isJobsLoading = useSelector(makeSelectIsLoading('jobs'));
  const isJobsInitialized = useSelector(isJobsInitializedSelector);
  const hasJobsInitializationFailed = useSelector(isJobsErroredSelector);
  const totalJobs = useSelector(totalJobsSelector);
  const jobs = useSelector(jobsSelector);
  const hasMoreJobs = useSelector(hasMoreJobsSelector);

  const statusOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Open', value: 'active' },
    { label: 'Expired', value: 'expired' },
    { label: 'Closed', value: 'closed' },
  ];
  const visibilityOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Visible', value: 'visible' },
    { label: 'Visible (manually)', value: 'visible_forced' },
    { label: 'Hidden (manually)', value: 'hidden_forced' },
    { label: 'Hidden (Job rules)', value: 'hidden' },
    { label: 'Not visible', value: 'not_visible' },
  ];
  const seniorityOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Internship', value: 'internship' },
    { label: 'Entry Level', value: 'entry_level' },
    { label: 'Associate', value: 'associate' },
    { label: 'Mid-Senior Level', value: 'mid_senior' },
    { label: 'Senior', value: 'senior' },
    { label: 'Director', value: 'director' },
    { label: 'Vice President', value: 'vice_president' },
    { label: 'CXO', value: 'cxo' },
  ];
  const featuredOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Featured', value: 'featured' },
    { label: 'Not featured', value: 'not_featured' },
  ];
  const expirationOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Next 7 days', value: '7' },
    { label: 'Next 30 days', value: '30' },
  ];
  const sourceOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Extracted', value: 'job_source' },
    { label: 'Posted', value: 'posted' },
  ];
  const equityOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Offering equity', value: 'true' },
    { label: 'Not offering equity', value: 'false' },
  ];

  const dispatch = useDispatch();
  const shouldRetrieveJobs = useMemo(
    () => isJobsInitialized && !isJobsLoading && !hasJobsInitializationFailed,
    [hasJobsInitializationFailed, isJobsInitialized, isJobsLoading],
  );
  const { jobsFilters, defaultFilter } = useJobsFilters({ forCompany });

  const hasJobsFilters = useMemo(() => {
    const filter = qsParse(location);
    return filter && Object.keys(filter).length !== 0;
  }, [location]);

  useEffect(() => {
    if (!isJobsInitialized && !isJobsLoading) {
      const params = qsParse(location);
      const page = parseInt(params.page || 1, 10);
      const hitsPerPage = defaultHitsPerPage * page;
      // Exclude visibility from filters when on company profile
      const filtersToUse = isCompanyProfile ? { ...jobsFilters } : jobsFilters;
      if (isCompanyProfile && filtersToUse.visibility) {
        delete filtersToUse.visibility;
      }
      dispatch(loadJobs({ page: 1, hitsPerPage, filters: filtersToUse, includeMatchesLists, isCompanyProfile }));
    }
  }, [
    shouldRetrieveJobs,
    dispatch,
    isJobsInitialized,
    isJobsLoading,
    location,
    jobsFilters,
    includeMatchesLists,
    isCompanyProfile,
  ]);

  useEffect(
    () => () => {
      dispatch(resetJobsList());
    },
    [dispatch],
  );

  const loadNextPage = useCallback(() => {
    if (shouldRetrieveJobs) {
      const params = qsParse(location);
      const page = parseInt(params.page || 1, 10) + 1;
      params.page = page.toString();
      history.replace({ pathname: location.pathname, search: qsStringify(params) });
      // Exclude visibility from filters when on company profile
      const filtersToUse = isCompanyProfile ? { ...jobsFilters } : jobsFilters;
      if (isCompanyProfile && filtersToUse.visibility) {
        delete filtersToUse.visibility;
      }
      dispatch(
        loadJobs({
          page,
          hitsPerPage: defaultHitsPerPage,
          filters: filtersToUse,
          includeMatchesLists,
          isCompanyProfile,
        }),
      );
    }
  }, [dispatch, history, jobsFilters, location, shouldRetrieveJobs, includeMatchesLists, isCompanyProfile]);

  const updateQueryString = useCallback(
    ({ filter, value }) => {
      const params = qsParse(location);
      if (value.length) {
        params[filter] = value;
      } else {
        delete params[filter];
      }
      return params;
    },
    [location],
  );

  const filterOrSortJobs = useCallback(
    ({ search }) => {
      const { locations, page, companyId, ...otherFilters } = search;
      const params = { ...otherFilters };

      if (locations) {
        params.locationIds = JSON.parse(atob(locations)).map(({ value }) => value);
      }

      // When forCompany is provided, always use its companyId and ignore any companyId from search params
      // This prevents showing jobs from other companies on company profile pages
      const finalFilters = { ...defaultFilter, ...params };
      if (forCompany?.id) {
        finalFilters.companyId = forCompany.id.toString();
      } else if (companyId) {
        // Only use search companyId if forCompany is not provided
        finalFilters.companyId = companyId;
      }

      const queryParams = { locations, ...otherFilters };
      // Don't include companyId in URL when forCompany is provided (it's implicit)
      if (!forCompany?.id && companyId) {
        queryParams.companyId = companyId;
      }

      history.replace({ pathname: location.pathname, search: qsStringify(queryParams) });
      dispatch(resetJobsList());
      dispatch(loadJobs({ filters: finalFilters, includeMatchesLists, isCompanyProfile }));
    },
    [dispatch, history, location.pathname, includeMatchesLists, isCompanyProfile, defaultFilter, forCompany],
  );

  const initialSortBy = useMemo(() => {
    const params = qsParse(location);
    let id = DEFAULT_FILTER.sortBy;
    let desc = DEFAULT_FILTER.sortDirection === 'desc';
    if (params.sortBy) {
      id = params.sortBy;
    }
    if (params.sortDirection) {
      desc = params.sortDirection === 'desc';
    }
    return { id, desc };
  }, [location]);

  return {
    updateQueryString,
    initialSortBy,
    statusOptions,
    visibilityOptions,
    seniorityOptions,
    featuredOptions,
    expirationOptions,
    sourceOptions,
    equityOptions,
    jobs,
    jobsFilters,
    hasJobsFilters,
    totalJobs,
    isJobsLoading,
    isJobsInitialized,
    hasJobsInitializationFailed,
    hasMoreJobs,
    filterOrSortJobs,
    loadNextPage,
  };
};

export default useJobs;
